import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import MotifCard from '@ey/motif-react-wrapper/Card';
import styled from 'styled-components';
import {labels, successMessageDisplayDuration} from '../../../util/uiconstants';
import {Link} from 'react-router-dom';
import {getMeCall} from '../../../actions/admin/mepermissionsactions';
import AccessDeniedMessage from '../../Common/AccessDeniedMessage/AccessDeniedMessage';
import {
	meCallPermissions,
	engagementsSearchRetainFlag
} from '../../../util/uiconstants';
import env from '../../../util/env';
import {postSyncDatabaseLocator} from '../../../actions/admin/databaselocatoractions';
import {getLocalizedAPIError} from '../common/util';
import {setIsModalOpen} from '../../../actions/modalstateactions';
import {clearErrors} from '../../../actions/erroractions';
import Toast from '../../Common/Toast/Toast';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {
	IconoirFolder,
	IconoirGoogleDocs,
	IconoirGroup,
	IconoirMessageText,
	IconoirUserBadgeCheck,
	IconoirRefreshDouble,
	IconoirSuitcase,
	IconoirMultiplePages,
	MotifNotificationIcSync24px,
} from '@ey-xd/motif-icon';

const Dashboardwrapper = styled.section`

	&.wrapper {
		position: relative;
		.toast {
			position: absolute;
			width: calc(100% - var(--px-68));
		}
	}

	.synclocator {
		display: flex;
		align-items: center;
		padding: var(--spacing-x-large) 0 var(--spacing-xx-large);

		h2 {
			font-size: var(--px-24);
			margin: 0;
			margin-right: auto;
		}

		.titleholder {
			display: flex;
			a {
				cursor: pointer;				
				display: inline-flex;
    			align-items: center;
				&:hover {
					text-decoration: none;
				}

				.motif-icon {
					display: inline-flex;
					svg {
						display: flex;
						vertical-align: middle;
					}
				}

				.title {
					font-size: var(--px-16);
					font-weight: bold;
					color: var(--blue-600);
					margin-left: var(--px-5);
				}
			}
		}
	}

	h1 {
		color: var(--neutrals-900);
	}

	.link-holder {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		grid-gap: var(--px-24);

		.motif-card-orientation-vertical {
			width: 100%;
		}
		.motif-card-border {
			border-radius: var(--px-8);
			display: flex;

			&:last-child {
				margin-right: 0;
			}

			&:hover {
				cursor: pointer;
			}

			&:hover .sub-item svg {
				fill: none;
			}

			&:hover .card-name {
				color: var(--gray-600);
			}

			.carditem {
				padding: var(--spacing-x-large);
				.sub-item {
					display: flex;

					.card-icon {
						padding-bottom: var(--spacing-x-large);
					}

					svg {
						min-width: var(--px-60);
						min-height: var(--px-60);
					}

					.card-name {
						font-size: var(--px-16);
						font-weight: bold;
						text-align: center;
					}
				}
			}
		}
	}
`;

function Dashboard() {
	const userPermissions = useSelector((store) => store.userPermissions);

	const [successMessage, setSuccessMessage] = useState();
	const [errorMessage, setErrorMessage] = useState();

	const successMessageDuration = successMessageDisplayDuration;

	let dispatch = useDispatch();

	useEffect(() => {
		localStorage.setItem(
			engagementsSearchRetainFlag.IsRetainEngagementsSearchResult,
			false
		);
		dispatch(getMeCall());
	}, []);

	const dashboardCards = [
		{
			iconName: IconoirFolder,
			tooltipText: 'Engagements',
			cardName: labels.engagementCard,
			permissions: [
				meCallPermissions.hasEngagementDefaultRole,
				meCallPermissions.canReadEngagement,
				meCallPermissions.canUpdateEngagement,
				meCallPermissions.canInviteEngagementMember,
				meCallPermissions.canUpdateEngagementUser
			],
			path: '/admin/engagements'
		},
		{
			iconName: IconoirMessageText,
			tooltipText: 'Messaging',
			cardName: labels.messagingCard,
			permissions: [
				meCallPermissions.canCreateUpdateMessaging,
				meCallPermissions.canDeleteMessaging,
				meCallPermissions.canReadMessaging
			],
			path: '/admin/messaging'
		},
		{
			iconName: IconoirGoogleDocs,
			tooltipText: labels.templatesCard,
			cardName: labels.templatesCard,
			permissions: [
				meCallPermissions.canReadTemplates,
				meCallPermissions.canUpdateTemplates,
				meCallPermissions.canDeleteTemplates
			],
			path: '/admin/template'
		},
		{
			iconName: IconoirGroup,
			tooltipText: 'Permission groups',
			cardName: labels.permissionGroupCard,
			permissions: [
				meCallPermissions.canReadPermissionsGroups,
				meCallPermissions.canCreateUpdatePermissionsGroups,
				meCallPermissions.canSyncPermissionsGroups
			],
			path: '/admin/permissiongroups'
		},
		{
			iconName: IconoirUserBadgeCheck,
			tooltipText: 'Manage access',
			cardName: labels.manageAccessCard,
			permissions: [
				meCallPermissions.canReadManageAccess,
				meCallPermissions.canCreateUpdateManageAccess
			],
			path: '/admin/manageaccess'
		},
		{
			iconName: IconoirRefreshDouble,
			tooltipText: 'Cache',
			cardName: 'Cache',
			permissions: [meCallPermissions.canReadDeleteCache],
			path: '/admin/cache'
		},
		{
			iconName: IconoirMultiplePages,
			tooltipText: 'Documents',
			cardName: 'Documents',
			permissions: [meCallPermissions.canAccessDocuments],
			path: '/admin/documents'
		},
		{
			iconName: IconoirSuitcase,
			tooltipText: 'Sharepoint workspaces',
			cardName: labels.sharepointWorkspacesCard,
			permissions: [
				meCallPermissions.canReadCollaborationSites,
				meCallPermissions.canCreateUpdateCollaborationSites,
				meCallPermissions.canDeleteCollaborationSites
			],
			path: '/admin/sharepointworkspaces'
		}
	];

	useEffect(() => {
		if (successMessage) {
			var timer = setTimeout(() => {
				setSuccessMessage('');
			}, successMessageDuration);

			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	const clearError = (isModelOpen) => {
		dispatch(clearErrors()).then(() => {
			dispatch(setIsModalOpen(isModelOpen));
		});
	};

	const onDBLocatorSyncClick = () => {
		const url =
			env.getURL('serviceUrlV2') + 'admin/dblocatorsynchronizationrequests';
		dispatch(postSyncDatabaseLocator(url))
			.then(() => {
				clearError(true);
				setSuccessMessage(labels.databaseLocatorSuccessMessage);
			})
			.catch((e) => {
				clearError(true);
				setErrorMessage(getLocalizedAPIError(e));
			});
	};

	const hasUserPermission = (cardInfo) => {
		if (
			userPermissions &&
			userPermissions.permissions &&
			userPermissions.permissions?.hasAdminDefaultRole
		) {
			let permKeys = Object.keys(userPermissions.permissions);
			let neededPerms = cardInfo.permissions;
			let allowedRoles = permKeys.filter((pkey) =>
				neededPerms.some((n) => n == pkey && userPermissions.permissions[pkey])
			);
			return allowedRoles.length;
		}
		return true;
	};

	const getContent = () => {
		let allowedCards = dashboardCards.filter((item) => hasUserPermission(item));
		return !allowedCards.length ? (
			<AccessDeniedMessage />
		) : (
			allowedCards.map((item, index) => (
				<MotifCard key={item.cardName + index}>
					<Link to={item.path}>
						<section className="carditem">
							<section className="sub-item">
								<MotifIcon
									name="card-icon"
									className="card-icon"
									iconFunction={item.iconName}
									title={item.tooltipText}
									size={24}
								/>
								{/* <SVGICONS
									styleName="large"
									name={item.iconName}
									hoverText={item.tooltipText}
								/> */}
							</section>
							<section className="sub-item">
								<span className="card-name">{item.cardName}</span>
							</section>
						</section>
					</Link>
				</MotifCard>
			))
		);
	};

	return (
		<Dashboardwrapper className="mainResponsiveWrapper wrapper">
			<section className="toast">
				{successMessage && (
					<Toast
						variant="success"
						duration={successMessageDuration}
						onClose={() => setSuccessMessage(undefined)}
					>
						{successMessage}
					</Toast>
				)}
				{errorMessage && (
					<Toast variant="error" onClose={() => setErrorMessage(undefined)}>
						{errorMessage}
					</Toast>
				)}
			</section>
			<section className="synclocator">
				<h2>
					<span>{labels.welcome}</span>
					<span>
						<b>{labels.welcomeadmin}</b>
					</span>
				</h2>
				<section className="titleholder">
					{userPermissions?.permissions?.canSyncDatabaseLocator && (
						<a onClick={onDBLocatorSyncClick}>
							<MotifIcon src={MotifNotificationIcSync24px} size='24'/>
							<span className="title">{labels.databaseLocatorCard}</span>
						</a>
					)}
				</section>
			</section>
			<section className="link-holder">{getContent()}</section>
		</Dashboardwrapper>
	);
}
export default Dashboard;
